import { TemplateData } from './types'

function russianDayNoun(value: number): 'день' | 'дня' | 'дней' {
  const normalized = Math.abs(value)
  const lastTwo = normalized % 100
  if (lastTwo >= 11 && lastTwo <= 14) return 'дней'
  const last = normalized % 10
  if (last === 1) return 'день'
  if (last >= 2 && last <= 4) return 'дня'
  return 'дней'
}

export function formatRussianTransitDays(value: string | number): string {
  const text = String(value).trim()
  if (!text) return ''
  const numbers = [...text.matchAll(/\d+/g)]
  if (numbers.length === 0) return text
  const lastNumber = Number(numbers.at(-1)?.[0])
  if (!Number.isSafeInteger(lastNumber)) return text
  return `${text} ${russianDayNoun(lastNumber)}`
}

export function formatRussianMessageDate(value: string): string {
  const text = value.trim()
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(text)
  if (!dateOnly) return text

  const year = Number(dateOnly[1])
  const month = Number(dateOnly[2])
  const day = Number(dateOnly[3])
  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) return text

  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date).replace(/\sг\.$/, '')
}

export function getDefaultPrenotifTemplateText(): string {
  return `Здравствуйте, \${firstName}. Спасибо за заказ. Посылка с курьером СДЭКа будет отправлена \${shipDate}, трек номер и время отправления сообщим дополнительно\n\n**ВАЖНО**: В чат, после выдачи заказа курьеру, вам будет отправлен код подтверждения для получения посылки.`
}

export function renderPrenotifTemplate(text: string, firstName: string, shipDate: string): string {
  return renderGreeting(text, firstName)
    .replace(/\$\{shipDate\}/g, formatRussianMessageDate(shipDate))
}

export function getDefaultCourierTemplateText(): string {
  return `Здравствуйте, \${firstName}!\n\nНа ваш заказ назначен курьер СДЭК.\n\nТрек-номер: **\${cdekNumber}**\nКод получения: **\${confirmationCode}**\n\nКурьер: **\${courierName}**\nТелефон: **\${courierPhone}**\nАвтомобиль: **\${courierCar}**\nГосномер: **\${courierPlate}**\n\nКурьер свяжется с вами перед доставкой.`
}

export function getDefaultCdekHandoffTemplateText(): string {
  return `Здравствуйте, \${firstName}!\n\nПосылка передана СДЭК и принята к перевозке.\n\nТрек-номер: **\${cdekNumber}**\nhttps://www.cdek.ru/ru/tracking/?order_id=\${cdekNumber}\n\nМы сообщим, когда посылка будет передана курьеру.`
}

/**
 * Renders the historical short automatic confirmation. Keep this only for
 * matching chat history created before the canonical-template migration.
 */
export function renderLegacyCdekHandoffTemplate(
  firstName: string,
  cdekNumber: string,
  transitPlan?: { days: number; plannedDeliveryDate: string } | null,
): string {
  const deliveryLine = transitPlan
    ? `\n\nСДЭК планирует доставку: **${formatRussianMessageDate(transitPlan.plannedDeliveryDate)}** (в пути **${formatRussianTransitDays(transitPlan.days)}**)`
    : ''
  return renderGreeting(getDefaultCdekHandoffTemplateText() + deliveryLine, firstName)
    .replace(/\$\{cdekNumber\}/g, cdekNumber)
}

export function renderCdekHandoffTemplate(
  firstName: string,
  cdekNumber: string,
  transitPlan?: { days: number; plannedDeliveryDate: string } | null,
): string {
  return renderHandoffTemplateWithText(getDefaultTemplateText(), {
    recipientName: firstName,
    firstName,
    shipDate: '',
    shipTime: '',
    region: 'Кузбасс',
    cdekNumber,
    transitDays: transitPlan ? String(transitPlan.days) : '',
    deliveryDate: transitPlan?.plannedDeliveryDate || '',
  }, transitPlan)
}

/** Render the manual handoff preview/payload with the same transit-evidence rule as Python. */
export function renderHandoffTemplateWithText(
  template: string,
  data: TemplateData,
  transitPlan?: { days: number; plannedDeliveryDate: string } | null,
): string {
  const verifiedTemplate = transitPlan
    ? template
    : template
      .replace(/^\*\*Заявленное время в пути:\*\*.*(?:\r?\n|$)/m, '')
      .replace(/^\*\*Ожидайте звонка от курьера:\*\*.*(?:\r?\n|$)/m, '')
  return renderTemplateWithText(verifiedTemplate, data)
}

export interface CourierTemplateData {
  firstName: string
  courierName: string
  courierPhone: string
  courierCar: string
  courierPlate: string
  cdekNumber: string
  confirmationCode?: string
}

export function renderCourierTemplate(text: string, data: CourierTemplateData): string {
  const withoutMissingCode = data.confirmationCode
    ? text
    : text.replace(/^.*\$\{confirmationCode\}.*(?:\r?\n|$)/gm, '')
  return renderGreeting(withoutMissingCode, data.firstName)
    .replace(/\$\{courierName\}/g, data.courierName)
    .replace(/\$\{courierPhone\}/g, data.courierPhone)
    .replace(/\$\{courierCar\}/g, data.courierCar)
    .replace(/\$\{courierPlate\}/g, data.courierPlate)
    .replace(/\$\{cdekNumber\}/g, data.cdekNumber)
    .replace(/\$\{confirmationCode\}/g, data.confirmationCode || '')
}

export function getDefaultTemplateText(): string {
  return `Здравствуйте, **\${firstName}**!\n\nБлагодарим Вас за заказ.\n\nДоставка будет осуществляться курьером **СДЭК** до квартиры (офиса).\nПосылка передана СДЭК и принята к перевозке.\n\n**Трек-номер для отслеживания:**\nhttps://www.cdek.ru/ru/tracking/?order_id=\${cdekNumber}\n\n**Заявленное время в пути:** \${transitDays} дней\n**Ожидайте звонка от курьера:** \${deliveryDate}\n\nИзделие упаковано в футляр (коробочку), который находится в герметичной **вакуумной упаковке**.\nДля вскрытия упаковки понадобятся **ножницы**.\nВ комплекте имеется **салфетка для полировки**.\n\nПриятного пользования и положительных эмоций!\n\nС уважением,\nювелирный магазин **«АЛМАЗ»**`
}

export function renderTemplateWithText(text: string, data: TemplateData): string {
  const transitDaysWithUnit = formatRussianTransitDays(data.transitDays)
  const shipDate = formatRussianMessageDate(data.shipDate)
  const deliveryDate = formatRussianMessageDate(data.deliveryDate)
  return renderGreeting(text, data.firstName)
    // Repair the canonical template and previously saved variants that used a
    // fixed noun after the placeholder.
    .replace(/\$\{transitDays\}\s+(?:день|дня|дней|дн\.?|д\.?)/g, transitDaysWithUnit)
    .replace(/\$\{recipientName\}/g, data.recipientName)
    .replace(/\$\{firstName\}/g, data.firstName)
    .replace(/\$\{shipDate\}/g, shipDate)
    .replace(/\$\{shipTime\}/g, data.shipTime)
    .replace(/\$\{region\}/g, data.region)
    .replace(/\$\{cdekNumber\}/g, data.cdekNumber)
    .replace(/\$\{transitDays\}/g, data.transitDays)
    .replace(/\$\{deliveryDate\}/g, deliveryDate)
}

export function renderTemplate(data: TemplateData): string {
  return renderTemplateWithText(getDefaultTemplateText(), data)
}

function renderGreeting(template: string, firstName: string): string {
  const name = firstName.trim()
  const salutation = name
    ? template
    : template.replace(/Здравствуйте,[ \t]*(?:\*\*)?\$\{firstName\}(?:\*\*)?[.!]?/g, 'Здравствуйте!')
  return salutation.replace(/\$\{firstName\}/g, () => name)
}
